import { createContext, useContext, useState, useCallback, ReactNode } from "react";

/* ═══════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════ */

export interface NavItemConfig {
  label: string;
  path: string;
  visible: boolean;
}

export interface NavCategoryConfig {
  id: string;
  label: string;
  visible: boolean;
  items: NavItemConfig[];
}

export interface CustomShortcutGroup {
  id: string;
  label: string;
  /** References to existing module paths */
  itemPaths: string[];
}

export interface NavigationConfig {
  /** Ordered categories with visibility and item order */
  categories: NavCategoryConfig[];
  /** Custom shortcut groups that reference existing modules */
  customGroups: CustomShortcutGroup[];
}

/* ═══════════════════════════════════════════
   CONTEXT
   ═══════════════════════════════════════════ */

interface NavigationConfigContextValue {
  config: NavigationConfig | null;
  setConfig: (config: NavigationConfig) => void;
  resetToDefault: () => void;
  isCustomized: boolean;
}

const NavigationConfigContext = createContext<NavigationConfigContextValue | null>(null);

export function NavigationConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfigState] = useState<NavigationConfig | null>(null);

  const setConfig = useCallback((c: NavigationConfig) => {
    setConfigState(c);
    try {
      localStorage.setItem("nav-config", JSON.stringify(c));
    } catch {}
  }, []);

  const resetToDefault = useCallback(() => {
    setConfigState(null);
    try {
      localStorage.removeItem("nav-config");
    } catch {}
  }, []);

  // Hydrate from localStorage on first render
  useState(() => {
    try {
      const stored = localStorage.getItem("nav-config");
      if (stored) {
        setConfigState(JSON.parse(stored));
      }
    } catch {}
  });

  return (
    <NavigationConfigContext.Provider
      value={{
        config,
        setConfig,
        resetToDefault,
        isCustomized: config !== null,
      }}
    >
      {children}
    </NavigationConfigContext.Provider>
  );
}

export function useNavigationConfig() {
  const ctx = useContext(NavigationConfigContext);
  if (!ctx) throw new Error("useNavigationConfig must be used within NavigationConfigProvider");
  return ctx;
}

/* ═══════════════════════════════════════════
   HELPERS — apply config to nav sections
   ═══════════════════════════════════════════ */

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Apply saved navigation config to the default nav sections.
 * Returns reordered/filtered sections + any custom shortcut groups injected.
 */
export function applyNavConfig(
  defaultSections: NavSection[],
  config: NavigationConfig | null
): NavSection[] {
  if (!config) return defaultSections;

  // Build a lookup of all items by path from default sections
  const allItems = new Map<string, NavItem>();
  for (const section of defaultSections) {
    for (const item of section.items) {
      allItems.set(item.path, item);
    }
  }

  const result: NavSection[] = [];

  // 1. Add custom shortcut groups first (they appear at top)
  for (const group of config.customGroups) {
    const items = group.itemPaths
      .map((p) => allItems.get(p))
      .filter(Boolean) as NavItem[];
    if (items.length > 0) {
      result.push({ label: group.label, items });
    }
  }

  // 2. Apply category order and visibility
  for (const catConfig of config.categories) {
    if (!catConfig.visible) continue;

    const defaultSection = defaultSections.find((s) => s.label === catConfig.id);
    if (!defaultSection) continue;

    // Filter and reorder items
    const orderedItems: NavItem[] = [];
    for (const itemConfig of catConfig.items) {
      if (!itemConfig.visible) continue;
      const found = defaultSection.items.find((i) => i.path === itemConfig.path);
      if (found) orderedItems.push(found);
    }

    // Add any items from default that weren't in config (safety)
    for (const item of defaultSection.items) {
      if (!orderedItems.find((i) => i.path === item.path)) {
        const itemConf = catConfig.items.find((ic) => ic.path === item.path);
        if (!itemConf || itemConf.visible) {
          orderedItems.push(item);
        }
      }
    }

    if (orderedItems.length > 0) {
      result.push({ label: catConfig.label, items: orderedItems });
    }
  }

  // 3. Add any default sections not in config (safety — no orphans)
  for (const section of defaultSections) {
    const inConfig = config.categories.find((c) => c.id === section.label);
    const inResult = result.find((r) => r.label === section.label);
    if (!inConfig && !inResult) {
      result.push(section);
    }
  }

  return result;
}
